import { AppShellContent } from "@assetwatch/ui";
import {
  createFileRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { useRetainedListSearch } from "../components/navigation/useRetainedListState";
import {
  mergeRequestsPageView,
  parseRequestsPageView,
} from "../lib/requestsPageView";
import { workRequestIdSchema } from "../lib/workIds";
import { RequestsScreen } from "../screens/RequestsScreen";

function RequestsRoute() {
  const pageView = Route.useSearch();
  const routeParams = useRouterState({
    select: (state) => state.matches.at(-1)?.params,
  });
  const pathRequestId = workRequestIdSchema.safeParse(
    routeParams && "workRequestId" in routeParams
      ? routeParams.workRequestId
      : undefined,
  );
  const linkedRequestId = pathRequestId.success
    ? pathRequestId.data
    : pageView.request;
  const navigate = useNavigate();
  // A direct link to one Request is explicit, so it never restores the
  // session's list filters; the list itself does.
  const { restoring } = useRetainedListSearch("requests", pageView, {
    restore: !pathRequestId.success && pageView.request === undefined,
  });

  useEffect(() => {
    if (pageView.request === undefined || pathRequestId.success) return;
    void navigate({
      to: "/requests/$workRequestId",
      params: { workRequestId: pageView.request },
      search: (current) =>
        mergeRequestsPageView(current, { request: undefined }),
      replace: true,
    });
  }, [navigate, pageView.request, pathRequestId.success]);

  if (restoring) return <AppShellContent pending>{null}</AppShellContent>;

  return (
    <>
      <RequestsScreen
        linkedRequestId={linkedRequestId}
        pageView={pageView}
        onPageViewChange={(patch, options) => {
          const nextRequestId = Object.hasOwn(patch, "request")
            ? patch.request
            : linkedRequestId;
          void navigate({
            to: nextRequestId ? "/requests/$workRequestId" : "/requests",
            replace: options?.replace,
            ...(nextRequestId
              ? { params: { workRequestId: nextRequestId } }
              : {}),
            search: (current) =>
              mergeRequestsPageView(current, {
                ...patch,
                request: undefined,
              }),
          });
        }}
      />
      <Outlet />
    </>
  );
}

export const Route = createFileRoute("/_app/requests")({
  component: RequestsRoute,
  validateSearch: parseRequestsPageView,
});
